<template>
  <div class="camera-capture">
    <!-- State 1: Initial — choose camera or file -->
    <div v-if="!previewUrl && !showCamera" class="capture-area">
      <i class="pi pi-camera"></i>
      <span>{{ t('Capturar Foto') }} {{ (index as any) !== null ? `#${(index as any) + 1}` : '' }}</span>
      <div class="capture-buttons">
        <Button
          :label="t('Abrir Câmera')"
          icon="pi pi-video"
          size="small"
          severity="primary"
          data-testid="camera-open-btn"
          @click="startCamera"
        />
        <span class="capture-or">{{ t('ou') }}</span>
        <!-- Galeria: SEM capture. Com capture="environment" o Android ignora o
             seletor do sistema e abre a câmera direto — era isso que impedia o
             usuário de escolher uma foto já existente. -->
        <input
          ref="fileInputRef"
          type="file"
          accept="image/*"
          class="camera-input"
          :data-testid="index !== null && index !== undefined ? `camera-capture-input-${index}` : 'camera-capture-input'"
          @change="handleFileChange"
        />
        <Button
          :label="t('Galeria')"
          icon="pi pi-images"
          size="small"
          severity="secondary"
          data-testid="camera-file-btn"
          @click="triggerFileInput"
        />
        <!-- Câmera nativa: só aparece quando o getUserMedia falhou (permissão
             negada, contexto não seguro). Mantém a câmera alcançável sem
             devolver o capture ao botão da galeria. -->
        <template v-if="cameraError">
          <input
            ref="nativeCameraInputRef"
            type="file"
            accept="image/*"
            capture="environment"
            class="camera-input"
            :data-testid="index !== null && index !== undefined ? `camera-native-input-${index}` : 'camera-native-input'"
            @change="handleFileChange"
          />
          <Button
            :label="t('Câmera do celular')"
            icon="pi pi-camera"
            size="small"
            severity="secondary"
            data-testid="camera-native-btn"
            @click="triggerNativeCamera"
          />
        </template>
      </div>
    </div>

    <!-- State 2: Live camera preview -->
    <div v-if="showCamera" class="camera-preview">
      <video ref="videoRef" autoplay playsinline class="camera-video" data-testid="camera-video"></video>
      <div class="camera-actions">
        <Button
          :label="t('Capturar')"
          icon="pi pi-camera"
          severity="success"
          size="small"
          data-testid="camera-capture-btn"
          @click="capturePhoto"
        />
        <Button
          :label="t('Fechar Câmera')"
          icon="pi pi-times"
          severity="secondary"
          size="small"
          outlined
          data-testid="camera-close-btn"
          @click="stopCamera"
        />
      </div>
    </div>

    <!-- State 3: Photo preview -->
    <div v-if="previewUrl" class="preview-area">
      <img :src="previewUrl" :alt="t('Preview da foto')" class="preview-image" />
      <div class="preview-actions">
        <Button
          :label="t('Refazer')"
          icon="pi pi-refresh"
          severity="secondary"
          size="small"
          outlined
          :data-testid="index !== null && index !== undefined ? `camera-capture-retake-${index}` : 'camera-capture-retake'"
          @click="handleRetake"
        />
      </div>
    </div>

    <p v-if="cameraError" class="camera-error">{{ cameraError }}</p>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../utils/DebugLogger';

const props = withDefaults(defineProps<{
  index?: number | null;
  autoOpen?: boolean;
}>(), {
  autoOpen: false,
});

const emit = defineEmits<{
  (e: 'photo-captured', file: File, index: number | null | undefined): void;
}>();

const { t } = useI18n();

const fileInputRef = ref<HTMLInputElement | null>(null);
const nativeCameraInputRef = ref<HTMLInputElement | null>(null);
const videoRef = ref<HTMLVideoElement | null>(null);
const previewUrl = ref<string | null>(null);
const showCamera = ref(false);
const cameraError = ref('');
let mediaStream: MediaStream | null = null;

// ── File input (fallback) ──────────────────────────────

const triggerFileInput = (): void => {
  fileInputRef.value?.click();
};

const triggerNativeCamera = (): void => {
  nativeCameraInputRef.value?.click();
};

const handleFileChange = (event: Event): void => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  log('CameraCapture', 'handleFileChange', 'File selected', { name: file.name, size: file.size });
  previewUrl.value = URL.createObjectURL(file);
  emit('photo-captured', file, props.index);
};

// ── Real camera (getUserMedia) ──────────────────────────

const startCamera = async (): Promise<void> => {
  cameraError.value = '';
  try {
    mediaStream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      audio: false,
    });
    // O <video> só existe depois de showCamera=true (v-if), por isso o srcObject
    // precisa ser atribuído no tick seguinte — antes disso videoRef ainda é null.
    showCamera.value = true;
    await nextTick();
    if (videoRef.value) {
      videoRef.value.srcObject = mediaStream;
      await videoRef.value.play();
    }
  } catch (err) {
    log('CameraCapture', 'startCamera', 'Camera error', err);
    const message = err instanceof Error ? err.message : String(err);
    if (message.includes('NotFound') || message.includes('DevicesNotFound')) {
      cameraError.value = t('Nenhuma câmera encontrada. Use "Galeria" ou "Câmera do celular".');
    } else if (message.includes('NotAllowed') || message.includes('PermissionDenied')) {
      cameraError.value = t('Permissão da câmera negada. Use "Galeria" ou "Câmera do celular".');
    } else {
      cameraError.value = t('Erro ao abrir câmera. Use "Galeria" ou "Câmera do celular".');
    }
  }
};

const capturePhoto = (): void => {
  if (!videoRef.value) return;
  const video = videoRef.value;
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth || 640;
  canvas.height = video.videoHeight || 480;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  canvas.toBlob((blob) => {
    if (!blob) return;
    const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
    log('CameraCapture', 'capturePhoto', 'Photo captured from camera', { size: file.size });
    previewUrl.value = URL.createObjectURL(file);
    emit('photo-captured', file, props.index);
    stopCamera();
  }, 'image/jpeg', 0.9);
};

const stopCamera = (): void => {
  if (mediaStream) {
    mediaStream.getTracks().forEach((t) => t.stop());
    mediaStream = null;
  }
  showCamera.value = false;
};

// ── Retake ──────────────────────────────────────────────

const handleRetake = (): void => {
  log('CameraCapture', 'handleRetake', 'Retaking photo', { index: props.index });
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
  }
  previewUrl.value = null;
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
  if (nativeCameraInputRef.value) {
    nativeCameraInputRef.value.value = '';
  }
};

onBeforeUnmount(() => {
  stopCamera();
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
  }
});

// Auto-open camera when autoOpen is true
onMounted(() => {
  if (props.autoOpen) {
    startCamera();
  }
});

defineExpose({ handleRetake, startCamera });
</script>

<style scoped>
.camera-capture {
  width: 100%;
}

.capture-area {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  border: 2px dashed var(--p-surface-300);
  border-radius: 8px;
  cursor: default;
  transition: border-color 0.2s;
  color: var(--p-text-muted-color);
}

.capture-area .pi-camera {
  font-size: 2rem;
}

.capture-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.capture-or {
  font-size: 0.8rem;
  color: var(--p-text-muted-color);
}

.camera-input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.camera-preview {
  position: relative;
  text-align: center;
}

.camera-video {
  width: 100%;
  max-height: 300px;
  border-radius: 8px;
  object-fit: cover;
  background: #000;
}

.camera-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 8px;
}

.camera-error {
  color: var(--p-red-500);
  font-size: 0.8rem;
  margin-top: 4px;
  text-align: center;
}

.preview-area {
  position: relative;
  text-align: center;
}

.preview-image {
  max-width: 100%;
  max-height: 200px;
  border-radius: 8px;
  object-fit: cover;
}

.preview-actions {
  margin-top: 8px;
}
</style>
